import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  Sparkles,
  MessageSquare,
  Send,
  Lightbulb,
  Target,
  Loader
} from 'lucide-react';
import { GoalIcon } from './goalIcons';
import { useChat } from '../hooks/useChat';
import { useDomainConfig } from '../hooks/useDomainConfig';

const WelcomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 40px 40px 32px 40px;
  text-align: center;
  max-width: 1100px;
  margin: 0 auto;
  box-sizing: border-box;
`;

const WelcomeIcon = styled(motion.div)`
  font-size: 80px;
  margin-bottom: 24px;
  background: linear-gradient(135deg, #1B8C3C 0%, #006200 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const WelcomeTitle = styled(motion.h1)`
  font-size: 48px;
  font-weight: 800;
  color: #2d3748;
  margin-bottom: 16px;
  background: linear-gradient(135deg, #2d3748 0%, #4a5568 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const WelcomeSubtitle = styled(motion.p)`
  font-size: 20px;
  color: #718096;
  line-height: 1.6;
  margin-bottom: 32px;
  max-width: 600px;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const SmartTipCard = styled(motion.div)`
  background: linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%);
  border: 2px solid #FED7AA;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 48px;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 600px;
  text-align: right;
  direction: rtl;

  svg {
    color: #F97316;
    flex-shrink: 0;
  }
`;

const SmartTipText = styled.p`
  font-size: 15px;
  color: #9A3412;
  line-height: 1.5;
  margin: 0;
  font-weight: 500;
`;

const GetStartedSection = styled(motion.div)`
  background: linear-gradient(135deg, #F0F7F7 0%, #E8F0F0 100%);
  border-radius: 16px;
  padding: 32px;
  border: 2px dashed #B8CECE;
  width: 100%;
  max-width: 900px;
`;

const GetStartedTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #0D1B2A;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  svg {
    color: #1B8C3C;
  }
`;

const GetStartedText = styled.p`
  font-size: 16px;
  color: #4a5568;
  line-height: 1.5;
  margin-bottom: 20px;
`;

const SampleQuestionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const SampleQuestionButton = styled(motion.button)`
  background: white;
  border: 2px solid #D0E0E0;
  border-radius: 12px;
  padding: 16px 20px;
  text-align: right;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: #0D1B2A;
  line-height: 1.4;
  direction: rtl;

  &:hover {
    border-color: #1B8C3C;
    background: #F0F7F7;
    transform: translateX(-2px);
  }

  svg {
    color: #1B8C3C;
    flex-shrink: 0;
  }
`;

const EditableQuestionContainer = styled.div`
  background: white;
  border: 2px solid #1B8C3C;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  direction: rtl;
`;

const EditableQuestionInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  color: #4a5568;
  background: transparent;
  text-align: right;
  direction: rtl;
  
  &::placeholder {
    color: #a0aec0;
  }
`;

const SendButton = styled(motion.button)`
  background: #1B8C3C;
  border: none;
  border-radius: 8px;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #006200;
  }

  svg {
    color: white;
    width: 16px;
    height: 16px;
  }
`;

// Mode Selector Styled Components
const ModeSelector = styled(motion.div)`
  display: flex;
  gap: 24px;
  margin-bottom: 48px;
  justify-content: center;
  direction: rtl;
`;

const ModeCard = styled(motion.button)`
  background: ${props => props.$active ?
    'linear-gradient(135deg, #1B8C3C 0%, #006200 100%)' :
    'white'};
  color: ${props => props.$active ? 'white' : '#0D1B2A'};
  border: 2px solid ${props => props.$active ? 'transparent' : '#D0E0E0'};
  border-radius: 16px;
  padding: 24px 32px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  min-width: 200px;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(13, 27, 42, 0.12);
  }

  svg {
    width: 32px;
    height: 32px;
  }
`;

const ModeTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const ModeDescription = styled.p`
  font-size: 14px;
  color: ${props => props.$active ? 'rgba(255,255,255,0.9)' : '#718096'};
  margin: 0;
  text-align: center;
`;

const GoalInputContainer = styled(motion.div)`
  width: 100%;
  max-width: 1000px;
  margin-bottom: 32px;
  direction: rtl;
`;

const GoalInputField = styled.textarea`
  width: 100%;
  min-height: 180px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  font-size: 18px;
  resize: vertical;
  direction: rtl;
  text-align: right;
  font-family: 'Inter', 'Noto Sans Arabic', sans-serif;

  &:focus {
    border-color: #1B8C3C;
    outline: none;
    box-shadow: 0 0 0 3px rgba(27,140,60, 0.1);
  }

  &::placeholder {
    color: #a0aec0;
  }

  &:disabled {
    background: #F0F7F7;
    cursor: not-allowed;
  }
`;

const GoalSubmitButton = styled(motion.button)`
  background: linear-gradient(135deg, #1B8C3C 0%, #006200 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 16px;
  cursor: pointer;
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const GoalSuggestionsContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 600px;
  direction: rtl;
`;

const GoalSuggestionCard = styled(motion.button)`
  background: white;
  border: 2px solid #D0E0E0;
  border-radius: 12px;
  padding: 20px;
  text-align: right;
  direction: rtl;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #1B8C3C;
    transform: translateX(-4px);
    box-shadow: 0 4px 12px rgba(27,140,60, 0.15);
  }
`;

const SuggestionLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 8px;
`;

const SuggestionQuery = styled.div`
  font-size: 14px;
  color: #718096;
`;

const GoalExplanation = styled.div`
  background: linear-gradient(135deg, #F0F4FF 0%, #E8ECFF 100%);
  border: 1px solid #E0E7FF;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  color: #4F46E5;
  font-size: 14px;
  direction: rtl;
  text-align: right;
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Predefined Goals Styled Components
const PredefinedGoalsContainer = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  width: 100%;
  max-width: 1000px;
  margin-bottom: 32px;
  direction: rtl;
  box-sizing: border-box;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const PredefinedGoalCard = styled(motion.button)`
  background: white;
  border: 2px solid #D0E0E0;
  border-radius: 16px;
  padding: 20px;
  text-align: right;
  direction: rtl;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  &:hover {
    border-color: #1B8C3C;
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(27,140,60, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const GoalCardIcon = styled.span`
  font-size: 32px;
  margin-bottom: 4px;
`;

const GoalCardTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
`;

const GoalCardDescription = styled.p`
  font-size: 13px;
  color: #718096;
  margin: 0;
  line-height: 1.4;
`;

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 600px;
  margin: 24px 0;
  direction: rtl;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e2e8f0;
  }

  span {
    padding: 0 16px;
    color: #a0aec0;
    font-size: 14px;
    font-weight: 500;
  }
`;

const CustomGoalLabel = styled.p`
  font-size: 14px;
  color: #718096;
  margin-bottom: 12px;
  text-align: right;
  direction: rtl;
`;

function WelcomeScreen({ setActiveTab }) {
  const {
    actions,
    dbId,
    chatMode,
    isRunningGoalAgent
  } = useChat();
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedQuestion, setEditedQuestion] = useState('');
  const [localGoalText, setLocalGoalText] = useState('');

  // Title, sample questions + goals come from the backend (domain pack), not hardcoded here.
  const { sampleQuestions, predefinedGoals: availableGoals, title, title_ar } = useDomainConfig(dbId);
  const welcomeTitle = title_ar || title || 'مساعد استعلام البيانات';

  // Mode selection handler
  const handleModeSelect = (mode) => {
    actions.setChatMode(mode);
    if (mode === 'normal') {
      setLocalGoalText('');
    }
  };

  // Predefined goal click handler — a card with a `route` opens that tab (e.g. Account 360 — a
  // single-account view the goal-agent shouldn't decompose); otherwise it runs the Goal Agent.
  const handlePredefinedGoalClick = (goalItem) => {
    if (goalItem.route && setActiveTab) { setActiveTab(goalItem.route); return; }
    setLocalGoalText(goalItem.goal);
    actions.setGoalText(goalItem.goal);
    actions.runGoalAgent(goalItem.goal);
  };

  // Goal submission handler — runs the Goal Agent
  const handleGoalSubmit = () => {
    if (localGoalText.trim() && !isRunningGoalAgent) {
      actions.setGoalText(localGoalText);
      actions.runGoalAgent(localGoalText);
    }
  };


  // The goal run now streams into the chat history (the welcome screen unmounts once messages
  // exist), so the goal picker just hides while a run is starting.
  const showGoalAgent = chatMode === 'goal' && isRunningGoalAgent;

  const handleQuestionClick = (question, index) => {
    setEditingIndex(index);
    setEditedQuestion(question);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      actions.sendMessage(editedQuestion);
      setEditingIndex(null);
      setEditedQuestion('');
    } else if (e.key === 'Escape') {
      setEditingIndex(null);
      setEditedQuestion('');
    }
  };

  const handleSendEdited = () => {
    actions.sendMessage(editedQuestion);
    setEditingIndex(null);
    setEditedQuestion('');
  };
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <WelcomeContainer>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <WelcomeIcon variants={itemVariants}>
          🎉
        </WelcomeIcon>

        <WelcomeTitle variants={itemVariants}>
          {welcomeTitle}
        </WelcomeTitle>

        <WelcomeSubtitle variants={itemVariants}>
          اطرح أسئلتك عن البيانات بالطريقة الطبيعية واحصل على إجابات فورية مع رسوم بيانية تفصيلية.
        </WelcomeSubtitle>

        {/* Mode Selector */}
        <ModeSelector variants={itemVariants}>
          <ModeCard
            $active={chatMode === 'normal'}
            onClick={() => handleModeSelect('normal')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <MessageSquare />
            <ModeTitle>محادثة عادية</ModeTitle>
            <ModeDescription $active={chatMode === 'normal'}>
              اطرح أسئلة مباشرة عن البيانات
            </ModeDescription>
          </ModeCard>

          <ModeCard
            $active={chatMode === 'goal'}
            onClick={() => handleModeSelect('goal')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Target />
            <ModeTitle>وضع الهدف</ModeTitle>
            <ModeDescription $active={chatMode === 'goal'}>
              أخبرني بهدفك وسأقترح استعلامات مناسبة
            </ModeDescription>
          </ModeCard>
        </ModeSelector>

        {/* Goal Mode UI */}
        {chatMode === 'goal' && !showGoalAgent && (
          <>
            {/* Predefined Goals */}
            {availableGoals.length > 0 && (
              <PredefinedGoalsContainer
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {availableGoals.map((goalItem, index) => (
                  <PredefinedGoalCard
                    key={index}
                    onClick={() => handlePredefinedGoalClick(goalItem)}
                    disabled={isRunningGoalAgent}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <GoalCardIcon><GoalIcon name={goalItem.icon} size={22} /></GoalCardIcon>
                    <GoalCardTitle>{goalItem.title}</GoalCardTitle>
                    <GoalCardDescription>{goalItem.description}</GoalCardDescription>
                  </PredefinedGoalCard>
                ))}
              </PredefinedGoalsContainer>
            )}

            {/* Or Divider */}
            <OrDivider>
              <span>أو اكتب هدفك</span>
            </OrDivider>

            {/* Custom Goal Input */}
            <GoalInputContainer
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <CustomGoalLabel>
                صف هدفك بكلماتك الخاصة:
              </CustomGoalLabel>
              <GoalInputField
                value={localGoalText}
                onChange={(e) => setLocalGoalText(e.target.value)}
                placeholder="مثال: أريد معرفة أين تتركز شكاوى مقدم الخدمة وأي المناطق الأكثر تأثراً بالانقطاعات"
                disabled={isRunningGoalAgent}
              />
              <GoalSubmitButton
                onClick={handleGoalSubmit}
                disabled={!localGoalText.trim() || isRunningGoalAgent}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isRunningGoalAgent ? (
                  <>
                    <Loader className="animate-spin" />
                    جارٍ التحليل...
                  </>
                ) : (
                  <>
                    <Sparkles />
                    حلّل الهدف
                  </>
                )}
              </GoalSubmitButton>
            </GoalInputContainer>
          </>
        )}

        {/* Normal Mode UI - Smart Tip */}
        {chatMode === 'normal' && (
          <SmartTipCard variants={itemVariants}>
            <Lightbulb size={20} />
            <SmartTipText>
              عندما يكون لسؤالك أكثر من إجابة محتملة، سأعرض لك البدائل المتاحة وأساعدك في تحسين سؤالك
            </SmartTipText>
          </SmartTipCard>
        )}

        {chatMode === 'normal' && (
        <GetStartedSection variants={itemVariants}>
          <GetStartedTitle>
            <MessageSquare />
            جاهز للبدء؟
          </GetStartedTitle>
          <GetStartedText>
            {sampleQuestions.length > 0
              ? "اختر من الأسئلة المقترحة أدناه أو اكتب سؤالك في مربع المحادثة واضغط Enter للإرسال:"
              : "اكتب سؤالك في مربع المحادثة أدناه واضغط Enter للإرسال:"
            }
          </GetStartedText>
          {sampleQuestions.length > 0 && (
            <>
              <SampleQuestionsContainer>
              {sampleQuestions.map((question, index) => (
                editingIndex === index ? (
                  <EditableQuestionContainer key={index}>
                    <SendButton
                      onClick={handleSendEdited}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Send />
                    </SendButton>
                    <EditableQuestionInput
                      value={editedQuestion}
                      onChange={(e) => setEditedQuestion(e.target.value)}
                      onKeyDown={handleKeyPress}
                      placeholder="اكتب سؤالك هنا..."
                      autoFocus
                    />
                  </EditableQuestionContainer>
                ) : (
                  <SampleQuestionButton
                    key={index}
                    onClick={() => handleQuestionClick(question, index)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Send size={16} />
                    {question}
                  </SampleQuestionButton>
                )
              ))}
              </SampleQuestionsContainer>
            </>
          )}
        </GetStartedSection>
        )}
      </motion.div>
    </WelcomeContainer>
  );
}

export default WelcomeScreen; 